import { importVocabularyBookApi } from "@/api/services/admin/learning-content";
import { CardContainer } from "@/components/commons/CardContainer";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { StatusTag } from "@/components/elements/StatusTag/StatusTag";
import { TopPage } from "@/components/elements/TopPage";
import { WordDataTable } from "@/features/learning-content/components/WordDataTable";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useUpdateVocabularyBookMemo } from "@/features/learning-content/hooks/useUpdateVocabularyBookMemo";
import { useVocabularyBook } from "@/features/learning-content/hooks/useVocabularyBook";
import { useVocabularyBookList } from "@/features/learning-content/hooks/useVocabularyBookList";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { validator } from "@/utils/validator";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, CircularProgress, Stack, Typography, styled } from "@mui/material";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { STATUS_LABEL } from "@/constants/enum";

type TWordDetailPageProps = {};

// Word detail page - displays vocabulary book basic info and word data table
export const WordDetailPage: FC<TWordDetailPageProps> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);

  const { data: industryData } = useIndustryDetail(id);
  const { data: vocabularyBookData, isLoading: isLoadingBook } = useVocabularyBook(id);
  const vocabList = useVocabularyBookList(id);
  const updateMemo = useUpdateVocabularyBookMemo(id);

  const industryName = industryData?.name ?? "";
  const isPublished = industryData?.isPublished ?? false;

  const handleMemoBlur = (value: string) => {
    if (!vocabularyBookData) return;
    updateMemo.mutate({
      vocabularyBookSpreadsheetUrl: vocabularyBookData.vocabularyBookSpreadsheetUrl ?? "",
      vocabularyBookMemo: value,
    });
  };

  const handleBack = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  const handleWordEdit = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.learningContentWordEdit, { id }));
    }
  };

  const handleOpenSheet = () => {
    if (vocabularyBookData?.vocabularyBookSpreadsheetUrl) {
      window.open(vocabularyBookData.vocabularyBookSpreadsheetUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleSync = async () => {
    if (!id) return;
    setIsSyncing(true);
    try {
      const result = await importVocabularyBookApi(id);
      if (result.data.message) toast.success(result.data.message);
      vocabList.refetch();
    } catch (error) {
      showError(error);
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoadingBook) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DetailWrapper gap={4}>
      <TopPage
        title={
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="head32Bold">{industryName}のコンテンツ管理 - 単語帳データ</Typography>
            <StatusTag status={isPublished ? "active" : "inactive"} title={isPublished ? STATUS_LABEL.ENABLED : "無公開"} variant="bold" />
          </Stack>
        }
        description={
          <Typography variant="body14">単語帳コンテンツを編集できます。学習者は単語帳としてだけでなく、単語練習としても活用できます。</Typography>
        }
        enablePageBack={false}
      />
      <MenuNavigate
        menuPrefix={
          <Box sx={{ p: "16px 16px 12px 16px" }}>
            <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
              {industryName}
            </Typography>
          </Box>
        }
        contents={[
          {
            title: "基本情報",
            content: <BasicInfoSection memo={vocabularyBookData?.vocabularyBookMemo} onMemoBlur={handleMemoBlur} isSaving={updateMemo.isPending} />,
          },
          {
            title: "単語帳データ",
            content: (
              <WordDataTable
                isLoading={vocabList.isLoading || isSyncing}
                isFetching={vocabList.isFetching}
                data={vocabList.data}
                totalPage={vocabList.totalPage}
                totalCount={vocabList.totalCount}
                page={vocabList.page}
                categoryCount={vocabList.categoryCount}
                showCategoryColumn
                showImageColumn
                onPageChange={vocabList.onPageChange}
                onOpenSheet={vocabularyBookData?.vocabularyBookSpreadsheetUrl ? handleOpenSheet : undefined}
                onSync={handleSync}
              />
            ),
          },
        ]}
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm-round" variant="secondary" onClick={handleBack}>
                戻る
              </Button>
              <Button size="sm-round" variant="outline" onClick={handleWordEdit}>
                単語帳の設定
              </Button>
            </Stack>
          </FooterContainer>
        }
      />
    </DetailWrapper>
  );
};

// Schema for inline memo validation (matches WordEditSchema.vocabularyBookMemo)
const memoSchema = validator.object().shape({
  vocabularyBookMemo: validator.string().trim().emptyToNull().max(1000).label("社内メモ").notRequired(),
});

type TMemoForm = { vocabularyBookMemo: string };

// Basic info section - editable vocabulary book memo with blur-to-save using react-hook-form + schema
const BasicInfoSection: FC<{ memo?: string | null; onMemoBlur?: (_value: string) => void; isSaving?: boolean }> = ({
  memo,
  onMemoBlur,
  isSaving,
}) => {
  const methods = useForm<TMemoForm>({
    defaultValues: { vocabularyBookMemo: memo ?? "" },
    resolver: yupResolver(memoSchema) as never,
    mode: "onBlur",
  });

  const handleBlur = async () => {
    const isValid = await methods.trigger("vocabularyBookMemo");
    if (!isValid) return;
    const value = methods.getValues("vocabularyBookMemo");
    if ((value ?? "").trim() !== (memo ?? "").trim()) {
      onMemoBlur?.(value);
    }
  };

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          基本情報
        </Typography>
        <FormProvider {...methods}>
          <FormField control={methods.control} name="vocabularyBookMemo" label="社内メモ">
            <Input fullWidth placeholder="社内メモを入力" onBlur={handleBlur} disabled={isSaving} />
          </FormField>
        </FormProvider>
      </Stack>
    </CardContainer>
  );
};

const DetailWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
